import { Component, inject, OnDestroy, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
  CommonService,
  Exception,
  StorageService,
  StorageType,
  ToasterService,
} from '@anddone/ui-utilities';
import { environment } from '../../../../environments/environment';
import { LoginService } from '../../../services/login-service';
import { MessageConstants } from '../../../constants/message.constant';
import { UserService } from '../../../services/user-service';

// emulateUrl may be a single origin, a comma-separated list, or a string[]
// (it varies by environment file and how it's injected at deploy time).
// Normalise all three to a trimmed, slash-stripped list of allowed admin origins.
const allowedEmulateOrigins: string[] = (
  Array.isArray(environment.emulateUrl)
    ? environment.emulateUrl
    : `${environment.emulateUrl}`.split(',')
)
  .map((origin) => origin.trim().replace(/\/+$/, ''))
  .filter(Boolean);

@Component({
  selector: 'app-emulate-agent',
  standalone: true,
  imports: [TranslateModule],
  templateUrl: './emulate-agent.html',
  styleUrl: './emulate-agent.scss',
})
export class EmulateAgent implements OnDestroy {
  showGuestUserErrorMessage = signal(false);
  showEmulationOriginErrorMessage = signal(false);
  isLoader = signal(false);
  private router = inject(Router);
  private loginService = inject(LoginService);
  private storageService = inject(StorageService);
  private toasterService = inject(ToasterService);
  private commonService = inject(CommonService);
  private userService = inject(UserService);
  constructor() {
    window.addEventListener('message', this.messageListener);
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.messageListener);
  }
  private readonly messageListener = (event: MessageEvent) => {
    if (!allowedEmulateOrigins.includes(event.origin)) {
      this.showEmulationOriginErrorMessage.set(true);
      return;
    }

    const guestUser = event.data;

    if (!guestUser?.isEmulated) {
      return;
    }

    this.storageService.save(StorageType.Session, 'emulatedUser', JSON.stringify(true));

    if (guestUser.changePassword) {
      this.showGuestUserErrorMessage.set(true);
      return;
    }

    setTimeout(() => {
      guestUser.adminUser = guestUser.userName;

      this.handleLoginResponse(guestUser);

      this.commonService.getAllCountryList();
      this.commonService.getStateList();
    }, 1000);
  };
  handleLoginResponse(loginResponse: any) {
    if (
      loginResponse.role !== null &&
      loginResponse.role !== '' &&
      loginResponse.changePassword === false
    ) {
      this.toasterService.showError(MessageConstants.login.loginPermission, 'full');
      return;
    }

    this.storageService.save(StorageType.Session, 'auth', JSON.stringify(loginResponse));

    this.storageService.save(
      StorageType.Session,
      'refreshToken',
      JSON.stringify(loginResponse.refreshToken)
    );

    this.storageService.save(StorageType.Session, 'timeOut', JSON.stringify(false));

    this.loginService.getloggedUsersDetails(loginResponse).subscribe({
      next: (res: any) => {
        this.storageService.save(StorageType.Session, 'userDetails', JSON.stringify(res));

        if (loginResponse.userId?.length > 0) {
          this.nonDefaultAdminUserDetails(loginResponse);
        } else {
          this.isLoader.set(false);
          this.toasterService.showSuccess(MessageConstants.login.loginSuccess, 'full');
          this.router.navigate(['/ally/users']);
        }
      },

      error: (e) => {
        this.isLoader.set(false);
        const toastMessage = Exception.exceptionMessage(e);

        this.toasterService.showError(toastMessage.join(', '), 'full');
      },
    });
  }

  nonDefaultAdminUserDetails(res: any) {
    const reqObj = {
      allyId: res.parentId,
      userId: res.userId,
    };

    this.userService.getUserDetailsById(reqObj).subscribe({
      next: (response: any) => {
        this.storageService.save(
          StorageType.Session,
          'nonDefaultAdminUser',
          JSON.stringify(response)
        );

        this.toasterService.showSuccess(MessageConstants.login.loginSuccess, 'full');

        this.router.navigate(['/ally/users']);
      },

      error: (error) => {
        const toastMessage = Exception.exceptionMessage(error);

        this.toasterService.showError(toastMessage.join(', '), 'full');
      },
    });
  }
}
